/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any,  */
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  ActionIcon,
  Box,
  Card,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { IconCopy, IconPlayerPause, IconPlayerPlay } from "@tabler/icons-react";
import { appShowNotification } from "@/utils/utils";

interface CustomAudioPlayerProps {
  recordItem: any;
  isQuickRecord?: boolean;
  renderActions?: () => React.ReactNode;
}
const CustomAudioPlayer = ({
  recordItem,
  isQuickRecord,
  renderActions,
}: CustomAudioPlayerProps) => {
  const audioRef = useRef<any>(null); // Reference to the audio element
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [url, setUrl] = useState<any>();
  const [recordCreated, setRecordCreated] = useState<any>();

  const clipboard = useClipboard();

  const getUrlAudio = async () => {
    if (isQuickRecord && !recordItem.cID) {
      const blobUrl = URL.createObjectURL(recordItem.blob);
      setUrl(blobUrl);
      return;
    }
    setUrl(recordItem.file);
    return;
  };

  useEffect(() => {
    getUrlAudio();
    const created = moment(recordItem.date).format("MM/DD/YYYY, HH:mm");
    setRecordCreated(created);
  }, [recordItem]);

  // Format time helper
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (audio.paused) {
      audio.play();
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    audioRef.current.currentTime = 0;
  };

  // Update current time as audio plays
  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    setCurrentTime(audio.currentTime);
  };

  const progressContainerRef = useRef<any>(null); // Reference to the progress bar container

  // Seek when dragging thumb
  const handleSeek = (e: any) => {
    const rect = progressContainerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    if (clickX < 0) {
      return;
    }
    const width = rect.width;
    const newTime = (clickX / width) * recordItem.duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleThumbDrag = (e: any) => {
    handleSeek(e);
  };

  const handleCopyText = () => {
    clipboard.copy(recordItem.text);
    appShowNotification({
      title: "Copied text to clipboard",
      type: "success",
    });
  };

  const handleCopySummary = () => {
    clipboard.copy(recordItem.summary);
    appShowNotification({
      title: "Copied summary to clipboard",
      type: "success",
    });
  };

  const cardClass = clsx(
    "card-record-item",
    recordItem.failedToUpload && "card-record-item-failed-to-upload",
    isQuickRecord && recordItem.cID && "card-record-item-quick-record-assigned",
    isQuickRecord &&
      !recordItem.cID &&
      "card-record-item-quick-record-unassigned",
  );

  return (
    <Card
      className={cardClass}
      w={isQuickRecord ? "100%" : "80%"}
      mx={"auto"}
      shadow={isQuickRecord ? "sm" : "md"}
    >
      <Box
        style={{
          width: "100%",
          maxWidth: "100%",
          margin: "auto",
          textAlign: "center",
        }}
      >
        {recordItem.file && (
          <audio
            ref={audioRef}
            src={url}
            preload="metadata"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleAudioEnd}
          />
        )}
        <Flex justify={"space-between"} align={"center"}>
          <Group gap={5} align={"center"}>
            {(isQuickRecord || recordItem.failedToUpload) && (
              <Text fw={500} fz={14}>
                {recordItem.name}
              </Text>
            )}
            {(isQuickRecord || recordItem.failedToUpload) && " · "}
            <Text fz={14}>{recordCreated}</Text>
            {recordItem.failedToUpload && (
              <Text fz={14} c={"red"}>
                (Failed to upload)
              </Text>
            )}
          </Group>
          {renderActions && renderActions()}
        </Flex>
        {recordItem.file && (
          <Flex align={"center"}>
            <ActionIcon onClick={togglePlayPause} variant="subtle" mr={10}>
              {!isPlaying ? (
                <IconPlayerPlay fill="#003554" />
              ) : (
                <IconPlayerPause />
              )}
            </ActionIcon>
            <Box
              ref={progressContainerRef}
              style={{
                position: "relative",
                width: "100%",
                height: 5,
                background: "#e0e0e0",
                borderRadius: 5,
                cursor: "pointer",
              }}
              onClick={handleSeek}
            >
              <Box
                style={{
                  width: `${(currentTime / recordItem.duration) * 100}%`,
                  height: 5,
                  borderTopLeftRadius: 10,
                  borderBottomLeftRadius: 10,
                  background: "#003554",
                }}
              />
              <Box
                style={{
                  position: "absolute",
                  top: "50%",
                  left: `${(currentTime / recordItem.duration) * 100}%`,
                  transform: "translate(-50%, -50%)",
                  width: 16,
                  height: 16,
                  background: "#003554",
                  borderRadius: "50%",
                  cursor: "grab",
                }}
                draggable
                onDrag={handleThumbDrag}
              />
            </Box>
          </Flex>
        )}
        {recordItem.file && (
          <Flex justify={"space-between"} align={"center"}>
            <Text fz={14} fw={500} c={"#003554"}>
              {formatTime(currentTime)}
            </Text>
            <Text fz={14} fw={500} c={"#003554"}>
              {formatTime(recordItem.duration)}
            </Text>
          </Flex>
        )}
        {recordItem.cID && recordItem.text && <Divider my={"md"} />}
        <Stack>
          {recordItem.cID && recordItem.summary && (
            <Group>
              <Text fz={14} ta={"left"} fw={600}>
                Summary
              </Text>
              <ActionIcon
                variant="subtle"
                onClick={handleCopySummary}
                size={20}
              >
                <IconCopy />
              </ActionIcon>
            </Group>
          )}
          {recordItem.cID && recordItem.summary && (
            <Text fz={14} ta={"left"}>
              {recordItem.summary}
            </Text>
          )}
          {recordItem.cID && recordItem.text && (
            <Group>
              <Text fz={14} ta={"left"} fw={600}>
                Transcript
              </Text>
              <ActionIcon variant="subtle" onClick={handleCopyText} size={20}>
                <IconCopy />
              </ActionIcon>
            </Group>
          )}
          {recordItem.cID && recordItem.text && (
            <Text fz={14} ta={"left"}>
              {recordItem.text}
            </Text>
          )}
        </Stack>
      </Box>
    </Card>
  );
};

export default CustomAudioPlayer;
